import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Partners from "@/pages/Partners";
import Cooperatives from "@/pages/Cooperatives";
import NewCooperative from "@/pages/NewCooperative";
import Invoices from "@/pages/Invoices";
import Commissions from "@/pages/Commissions";
import Simulator from "@/pages/Simulator";
import SimulatorGroupA from "@/pages/SimulatorGroupA";
import BulkSimulator from "@/pages/BulkSimulator";
import CooperativesManagement from "@/pages/CooperativesManagement";
import Users from "@/pages/Users";
import PowerPlants from "@/pages/PowerPlants";
import PowerPlantsBilling from "@/pages/PowerPlantsBilling";
import PowerPlantReport from "@/pages/PowerPlantReport";
import Analytics from "@/pages/Analytics";
import ConsultaCooperado from "@/pages/ConsultaCooperado";
import ConsultaParceiro from "@/pages/ConsultaParceiro";
import DistributorSettings from "@/pages/DistributorSettings";

// ============================================================
// BACKEND URL RESOLUTION
// ------------------------------------------------------------
// When the user visits the app on their CUSTOM domain
// (e.g. https://enercooper.app.br), we want all API calls to go
// through that SAME ORIGIN (/api/*) instead of the Emergent-managed
// subdomain (green-units.emergent.host). This avoids cross-origin
// connection issues caused by Cloudflare bot detection / regional
// edge routing problems that intermittently break green-units.* for
// some clients with net::ERR_CONNECTION_CLOSED.
//
// Logic:
//   - If running in a browser AND the current origin is NOT an
//     emergent.host / preview domain → use same-origin (/api/*).
//   - Otherwise (preview, localhost, or fallback) → use the
//     REACT_APP_BACKEND_URL value injected at build time.
// ============================================================
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function resolveBackendUrl() {
  if (typeof window === 'undefined') return ENV_BACKEND_URL;
  const origin = window.location.origin;
  // Same-origin preferred whenever we're on a non-Emergent custom domain
  const isEmergentManaged = /emergent(agent|\.host)/i.test(origin);
  if (!isEmergentManaged) {
    return origin; // e.g. https://enercooper.app.br
  }
  return ENV_BACKEND_URL;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

// ============================================================
// ONE-TIME SERVICE-WORKER CACHE BUSTER (2026-05-21)
// ------------------------------------------------------------
// Earlier versions of /service-worker.js were caching /static/js/main.js
// with the OLD backend URL baked into the bundle, so users kept calling
// green-units.emergent.host even after a redeploy.
//
// This block runs ONCE per browser, evicts every cached bundle from the
// SW, unregisters the old worker and reloads the tab so the freshly
// served JS is the one that talks to enercooper.app.br/api.
//
// After this code has run once, the flag stays in localStorage so we
// don't reload on every visit.
// ============================================================
(function flushStaleServiceWorker() {
  if (typeof window === 'undefined') return;
  const FLAG = 'sw_cache_buster_2026_05_21';
  if (localStorage.getItem(FLAG)) return;

  let didWork = false;

  // 1. Unregister every controlling service worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => {
        r.unregister();
        didWork = true;
      });
    }).catch(() => {});
  }

  // 2. Delete every Cache Storage bucket
  if (typeof caches !== 'undefined' && caches.keys) {
    caches.keys().then((names) => {
      names.forEach((n) => {
        caches.delete(n);
        didWork = true;
      });
    }).catch(() => {});
  }

  // 3. Set the flag + reload (with a small delay so the unregister calls
  //    actually complete before the page refresh)
  setTimeout(() => {
    localStorage.setItem(FLAG, '1');
    if (didWork) {
      window.location.reload();
    }
  }, 400);
})();

export const axiosInstance = axios.create({
  baseURL: API,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/" />;
  // Perfis de cadastro restrito só enxergam a própria tela
  const stored = JSON.parse(localStorage.getItem("user") || "null");
  const only = { powerplant_registration: "/powerplants", partner_registration: "/partners" }[stored?.role];
  if (only && window.location.pathname !== only) return <Navigate to={only} />;
  return children;
}

// Master = Admin + acesso a Usinas, Relatório Usinas, Simulador em Lote e Configurações
const normalizeUser = (u) => {
  if (!u) return u;
  if (u.role === "master") return { ...u, role: "admin", is_master: true };
  return u;
};

function MasterRoute({ user, children }) {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/" />;
  if (user && user.role === "admin" && !user.is_master) return <Navigate to="/dashboard" />;
  return children;
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(normalizeUser(JSON.parse(storedUser)));
    }
    if (localStorage.getItem("token")) {
      axiosInstance.get("/auth/me").then((res) => {
        const fresh = normalizeUser(res.data);
        localStorage.setItem("user", JSON.stringify(fresh));
        setUser(fresh);
      }).catch(() => {});
    }
  }, []);

  const handleLogin = (rawUser, token) => {
    const userData = normalizeUser(rawUser);
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.success("Logout realizado com sucesso");
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Login onLogin={handleLogin} />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/partners"
            element={
              <PrivateRoute>
                <Partners user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/cooperatives"
            element={
              <PrivateRoute>
                <Cooperatives user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/cooperatives/new"
            element={
              <PrivateRoute>
                <NewCooperative user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <PrivateRoute>
                <Invoices user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/commissions"
            element={
              <PrivateRoute>
                <Commissions user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/simulator"
            element={
              <PrivateRoute>
                <Simulator user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/simulator-group-a"
            element={
              <PrivateRoute>
                <SimulatorGroupA user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/simulator-bulk"
            element={
              <MasterRoute user={user}>
                <BulkSimulator user={user} onLogout={handleLogout} />
              </MasterRoute>
            }
          />
          <Route
            path="/cooperatives-management"
            element={
              <PrivateRoute>
                <CooperativesManagement user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <Users user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/powerplants"
            element={
              user?.role === 'powerplant_registration' ? (
                <PrivateRoute><PowerPlants user={user} onLogout={handleLogout} /></PrivateRoute>
              ) : (
                <MasterRoute user={user}>
                  <PowerPlants user={user} onLogout={handleLogout} />
                </MasterRoute>
              )
            }
          />
          <Route
            path="/powerplants-billing"
            element={
              <MasterRoute user={user}>
                <PowerPlantsBilling user={user} onLogout={handleLogout} />
              </MasterRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Analytics user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/consulta-cooperado"
            element={
              <PrivateRoute>
                {user?.role === 'admin'
                  ? <ConsultaCooperado user={user} onLogout={handleLogout} />
                  : <Navigate to="/dashboard" />}
              </PrivateRoute>
            }
          />
          <Route
            path="/consulta-parceiro"
            element={
              <PrivateRoute>
                {user?.role === 'admin'
                  ? <ConsultaParceiro user={user} onLogout={handleLogout} />
                  : <Navigate to="/dashboard" />}
              </PrivateRoute>
            }
          />
          <Route
            path="/powerplant-report"
            element={
              <PrivateRoute>
                <PowerPlantReport user={user} onLogout={handleLogout} />
              </PrivateRoute>
            }
          />
          <Route
            path="/distributor-settings"
            element={
              <MasterRoute user={user}>
                <DistributorSettings user={user} onLogout={handleLogout} />
              </MasterRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;